import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../ModalShell";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import SearchInput from "../TailwindComponents/SearchInput";
import ProfileImage from "../../Header/ProfileImage";
import { normalise } from '../access/accessModel';
import {
  PiUsersLight,
  PiCheckCircleFill,
  PiCircleLight,
  PiSparkleLight,
  PiWarningLight,
} from "react-icons/pi";
import {
  AddonAssignment,
  OrgMember,
} from "../../../store/actions/PlanActions";
import Spinner from '../TailwindComponents/Spinner';

/** Format snake_case role strings → Title Case (e.g. "full_access" → "Full Access") */
const formatRole = (role: string): string =>
  role
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

interface AddonAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  addon: AddonAssignment | null;
  members: OrgMember[];
  onAssign: (orgLicenseId: number, userId: number) => Promise<boolean>;
  onRevoke: (orgLicenseId: number, userId: number) => Promise<boolean>;
}

const AddonAssignmentModal: React.FC<AddonAssignmentModalProps> = ({
  open,
  onClose,
  addon,
  members,
  onAssign,
  onRevoke,
}) => {
  const { t } = useTranslation();
  const [loadingUserId, setLoadingUserId] = useState<number | null>(null);
  const [assignedIds, setAssignedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);

  // Sync local assigned state from addon prop
  useEffect(() => {
    if (addon) {
      setAssignedIds(new Set(addon.assigned_users.map((u) => u.user_id)));
    }
  }, [addon]);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setConfirmRevoke(null);
    }
  }, [open]);

  // Filter members by search
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      (m) =>
        (m.name && m.name.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q)) ||
        (m.role && formatRole(m.role).toLowerCase().includes(q))
    );
  }, [members, searchQuery]);

  // Sort: assigned users first
  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      const aAssigned = assignedIds.has(a.id) ? 0 : 1;
      const bAssigned = assignedIds.has(b.id) ? 0 : 1;
      return aAssigned - bAssigned;
    });
  }, [filteredMembers, assignedIds]);

  if (!addon) return null;

  const usedSeats = assignedIds.size;
  const totalSeats = addon.quantity;
  const hasAvailable = usedSeats < totalSeats;

  const handleToggle = async (userId: number) => {
    // If removing, require confirmation first
    if (assignedIds.has(userId)) {
      if (confirmRevoke !== userId) {
        setConfirmRevoke(userId);
        return;
      }
      // Confirmed - proceed with revoke
      setConfirmRevoke(null);
    }

    setLoadingUserId(userId);
    try {
      if (assignedIds.has(userId)) {
        const ok = await onRevoke(addon.org_license_id, userId);
        if (ok) {
          setAssignedIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        }
      } else {
        if (!hasAvailable) return;
        const ok = await onAssign(addon.org_license_id, userId);
        if (ok) {
          setAssignedIds((prev) => new Set(prev).add(userId));
        }
      }
    } finally {
      setLoadingUserId(null);
    }
  };

  return (
    <ModalShell
      isOpen={open}
      onClose={onClose}
      title={<>{addon.display_name || addon.name} - {t("Assign users")}</>}
      icon={<PiSparkleLight />}
      size="xl"
      bodyClassName="flex flex-col overflow-hidden"
      footer={
        <Button variant="neutral" size="small" onClick={onClose}>
          {t("Done")}
        </Button>
      }
    >
              {/* Seat usage bar */}
              <div className="px-5 pt-4 pb-3">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-fg-muted flex items-center gap-1">
                    <PiUsersLight className="h-3.5 w-3.5" />
                    {t("Seats")}
                  </span>
                  <span className="text-fg-strong">
                    {usedSeats} / {totalSeats} {t("used")}
                  </span>
                </div>
                <div className="w-full bg-surface-sunken rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      usedSeats >= totalSeats
                        ? "bg-danger-500"
                        : usedSeats > totalSeats * 0.75
                        ? "bg-warning-500"
                        : "bg-primary-500"
                    }`}
                    style={{ width: `${Math.min(100, (usedSeats / totalSeats) * 100)}%` }}
                  />
                </div>
                {!hasAvailable && (
                  <p className="text-xs text-danger-500 dark:text-danger-400 mt-1">
                    {t("All seats are assigned. Remove a user or add more seats to assign additional users.")}
                  </p>
                )}
              </div>

              {/* Search bar */}
              <div className="px-5 pb-3">
                <SearchInput
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={t("Search by name, email or role...")}
                />
              </div>

              {/* Members list */}
              <div className="px-5 pb-3 flex-1 overflow-y-auto min-h-0">
                <div className="space-y-1">
                  {sortedMembers.map((member) => {
                    const isAssigned = assignedIds.has(member.id);
                    const isLoading = loadingUserId === member.id;
                    const canToggle = isAssigned || hasAvailable;
                    const showRevokeConfirm = confirmRevoke === member.id;

                    return (
                      <div key={member.id}>
                        <button
                          onClick={() => canToggle && handleToggle(member.id)}
                          disabled={isLoading || (!isAssigned && !hasAvailable)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg text-start transition-colors ${
                            isAssigned
                              ? "bg-primary-50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800"
                              : canToggle
                              ? "hover:bg-surface-sunken/70 border border-transparent"
                              : "opacity-50 cursor-not-allowed border border-transparent"
                          }`}
                        >
                          {/* Toggle icon */}
                          <div className="flex-shrink-0">
                            {isLoading ? (
                              <Spinner size="glyph" className="!h-5 !w-5" />
                            ) : isAssigned ? (
                              <PiCheckCircleFill className="h-5 w-5 text-primary-500" />
                            ) : (
                              <PiCircleLight className="h-5 w-5 text-fg-muted" />
                            )}
                          </div>

                          {/* Avatar */}
                          <ProfileImage
                            profilePicture={normalise(member).avatar}
                            email={member.email}
                            initials={
                              member.name
                                ? member.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()
                                    .slice(0, 2)
                                : (member.email?.[0] || "?").toUpperCase()
                            }
                            name={member.name}
                            size="h-8 w-8"
                          />

                          {/* User info */}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-fg-strong truncate">
                              {member.name || member.email}
                            </p>
                            {member.name && (
                              <p className="text-xs text-fg-muted truncate">
                                {member.email}
                              </p>
                            )}
                          </div>

                          {/* Role badge */}
                          {member.role && (
                            <Badge color="gray" className="flex-shrink-0">
                              {formatRole(member.role)}
                            </Badge>
                          )}
                        </button>

                        {/* Revoke confirmation banner */}
                        {showRevokeConfirm && (
                          <div className="ms-11 mt-1 mb-1 flex items-center gap-2 rounded-lg border border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 px-3 py-2">
                            <PiWarningLight className="h-4 w-4 text-warning-600 dark:text-warning-400 flex-shrink-0" />
                            <p className="text-xs text-warning-700 dark:text-warning-300 flex-1">
                              {t("Removing access may affect this user's workflow. Click again to confirm.")}
                            </p>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmRevoke(null); }}
                              className="text-xs text-fg-muted hover:text-fg-body font-medium"
                            >
                              {t("Cancel")}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {filteredMembers.length === 0 && members.length > 0 && (
                  <p className="text-sm text-fg-muted text-center py-6">
                    {t("No members match your search")}
                  </p>
                )}

                {members.length === 0 && (
                  <p className="text-sm text-fg-muted text-center py-6">
                    {t("No members found in your organization")}
                  </p>
                )}
              </div>

    </ModalShell>
  );
};

export default AddonAssignmentModal;
